import React, { useEffect } from "react";
import * as yup from 'yup';
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, FormGroup, Label, Input, FormFeedback, Alert } from "reactstrap";

const schema = yup.object().shape({
  seats: yup
    .number()
    .typeError('Seats must be a number')
    .required('Number of seats is required')
    .positive('Seats must be a positive number')
    .integer('Seats must be a whole number')
    .min(1, 'Seats must be at least 1')
});

const EditSeatsModal = ({ open, onClose, organization, onSubmit }) => {
  const { control, reset, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      seats: `${organization?.cmr_seats}`
    }
  });

  useEffect(() => {
    if (open && organization) {
      reset({ seats: `${organization?.cmr_seats || ""}` });
    }
  }, [open, organization, reset]);

  const handleFormSubmit = async (values) => {
    try {
      await onSubmit(organization, parseInt(values.seats));
      reset();
      onClose();
    } catch (e) {
      console.error('Error updating seats:', e);
    }
  };

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        <span style={{ fontSize: "18px", fontWeight: "700" }}>
          Edit Allowed Seats
        </span>
      </ModalHeader>
      <ModalBody>
        {organization && (
          <Alert color="info" className="mb-3">
            <div><strong>Organization:</strong> {organization.name || 'N/A'}</div>
            <div><strong>Website:</strong> {organization.website || 'N/A'}</div>
            <div><strong>Type:</strong> {organization.type || 'N/A'}</div>
            <div><strong>Current Allowed Seats:</strong> {organization.plan_details?.seats || 0}</div>
          </Alert>
        )}

        <FormGroup>
          <Label>Allowed Seats *</Label>
          <Controller
            name="seats"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Input
                type="number"
                value={value}
                onChange={onChange}
                invalid={!!errors?.seats}
                placeholder="Enter number of seats"
                min="1"
                step="1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(handleFormSubmit)();
                  }
                  // Prevent decimal point and negative sign
                  if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
              />
            )}
          />
          {errors?.seats && (
            <FormFeedback style={{ display: 'block' }}>{errors.seats.message}</FormFeedback>
          )}
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <button
          className='btn btn-outline-dark'
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <Button
          color="primary"
          onClick={handleSubmit(handleFormSubmit)}
          type='submit'
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Spinner size="sm" color="light" />
          ) : (
            "Update Seats"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EditSeatsModal;
